import { AssetDocumentArea, AssetInventoryDocumentResponse, DocumentGroup, assetInventoryDocumentsApi } from "@/api";
import { HANDLE_ERROR_LOCALLY_HEADER } from "@/api/middleware/errorMiddleware";
import {
  ASSET_INVENTORY_ZONE_QUERY_KEYS,
  AssetInventoryZone,
} from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { QueryKey } from "@/query/Query";
import { AssetInventorySubTab } from "@/router/ClientRoutes";
import { LeafTranslationKeys, TranslationType } from "@/translations";

type DropzoneTranslationKey = LeafTranslationKeys<Pick<TranslationType, "dropzone">>;
type ClientsTranslationKey = LeafTranslationKeys<Pick<TranslationType, "clients">>;

export interface AssetInventoryUploadArea {
  /** Backing document group — also the DnD zone / droppable id. */
  group: AssetInventoryZone;
  /** Translation key of the area's display name (section title, DnD zone name). */
  titleKey: DropzoneTranslationKey;
  /** The asset inventory section that owns the upload area — target of the "go to section" link. */
  owningSubTab: AssetInventorySubTab;
  /** Translation key of the owning section's navigation label (link text). */
  owningSectionLabelKey: ClientsTranslationKey;
  /** react-query key root of the area's document list ([listQueryKey, clientId]). */
  listQueryKey: QueryKey;
  listDocuments: (clientId: string) => Promise<AssetInventoryDocumentResponse[]>;
  /** Uploads one file into the area; errors are left to the caller (deferred-save pattern). */
  uploadDocument: (clientId: string, file: File) => Promise<unknown>;
}

function toUploadBody(file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } };
}

const ASSETS_SECTION = {
  owningSubTab: AssetInventorySubTab.Assets,
  owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.assets",
} as const;

const OTHER_PROPERTY_RIGHTS_SECTION = {
  owningSubTab: AssetInventorySubTab.OtherPropertyRights,
  owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.otherPropertyRights",
} as const;

const DONATIONS_SECTION = {
  owningSubTab: AssetInventorySubTab.Donations,
  owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.donations",
} as const;

/** An upload area of the assets, the other property rights or the donations tab; all of them share the area-parameterised document endpoints. */
function assetArea<Zone extends AssetInventoryZone, Key extends QueryKey>(
  group: Zone,
  area: AssetDocumentArea,
  titleKey: DropzoneTranslationKey,
  listQueryKey: Key,
  section: typeof ASSETS_SECTION | typeof OTHER_PROPERTY_RIGHTS_SECTION | typeof DONATIONS_SECTION = ASSETS_SECTION
) {
  return {
    group,
    titleKey,
    ...section,
    listQueryKey,
    listDocuments: (clientId: string) => assetInventoryDocumentsApi.getAssetInventoryAssetDocuments({ clientId, area }),
    uploadDocument: (clientId: string, file: File) =>
      assetInventoryDocumentsApi.uploadAssetInventoryAssetDocument({ clientId, area }, toUploadBody(file)),
  };
}

/**
 * Upload area registry, keyed by zone and typed against ASSET_INVENTORY_ZONE_QUERY_KEYS
 * (DropZoneDnd.types.ts): a zone registered there without an entry here, a `group` that does not
 * match its key, or a `listQueryKey` that differs from the one the cross-zone invalidation uses
 * is a compile error. A new upload area needs its entry here plus a position in
 * AREA_DISPLAY_POSITIONS below.
 */
const AREAS_BY_ZONE: {
  [Zone in AssetInventoryZone]: AssetInventoryUploadArea & {
    group: Zone;
    listQueryKey: (typeof ASSET_INVENTORY_ZONE_QUERY_KEYS)[Zone];
  };
} = {
  [DocumentGroup.AssetInventoryBankStatement]: {
    group: DocumentGroup.AssetInventoryBankStatement,
    titleKey: "dropzone.dnd.zoneAssetInventoryBankStatement",
    owningSubTab: AssetInventorySubTab.PersonalData,
    owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.personalData",
    listQueryKey: QueryKey.AssetInventoryBankStatements,
    listDocuments: (clientId) => assetInventoryDocumentsApi.getAssetInventoryBankStatements({ clientId }),
    uploadDocument: (clientId, file) =>
      assetInventoryDocumentsApi.uploadAssetInventoryBankStatement({ clientId }, toUploadBody(file)),
  },
  [DocumentGroup.AssetInventoryDependants]: {
    group: DocumentGroup.AssetInventoryDependants,
    titleKey: "dropzone.dnd.zoneAssetInventoryDependants",
    owningSubTab: AssetInventorySubTab.Relatives,
    owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.relatives",
    listQueryKey: QueryKey.AssetInventoryDependantDocuments,
    listDocuments: (clientId) => assetInventoryDocumentsApi.getAssetInventoryDependantDocuments({ clientId }),
    uploadDocument: (clientId, file) =>
      assetInventoryDocumentsApi.uploadAssetInventoryDependantDocument({ clientId }, toUploadBody(file)),
  },
  [DocumentGroup.AssetInventoryIncome]: {
    group: DocumentGroup.AssetInventoryIncome,
    titleKey: "dropzone.dnd.zoneAssetInventoryIncome",
    owningSubTab: AssetInventorySubTab.Income,
    owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.income",
    listQueryKey: QueryKey.AssetInventoryIncomeProofs,
    listDocuments: (clientId) => assetInventoryDocumentsApi.getAssetInventoryIncomeProofs({ clientId }),
    uploadDocument: (clientId, file) =>
      assetInventoryDocumentsApi.uploadAssetInventoryIncomeProof({ clientId }, toUploadBody(file)),
  },
  [DocumentGroup.AssetInventoryExpenses]: {
    group: DocumentGroup.AssetInventoryExpenses,
    titleKey: "dropzone.dnd.zoneAssetInventoryExpenses",
    owningSubTab: AssetInventorySubTab.Expenses,
    owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.expenses",
    listQueryKey: QueryKey.AssetInventoryExpenseProofs,
    listDocuments: (clientId) => assetInventoryDocumentsApi.getAssetInventoryExpenseProofs({ clientId }),
    uploadDocument: (clientId, file) =>
      assetInventoryDocumentsApi.uploadAssetInventoryExpenseProof({ clientId }, toUploadBody(file)),
  },
  [DocumentGroup.AssetInventoryOwnClaims]: {
    group: DocumentGroup.AssetInventoryOwnClaims,
    titleKey: "dropzone.dnd.zoneAssetInventoryOwnClaims",
    owningSubTab: AssetInventorySubTab.OwnClaims,
    owningSectionLabelKey: "clients.client.debtSettlement.assetInventory.ownClaims",
    listQueryKey: QueryKey.AssetInventoryOwnClaimDocuments,
    listDocuments: (clientId) => assetInventoryDocumentsApi.getAssetInventoryOwnClaimDocuments({ clientId }),
    uploadDocument: (clientId, file) =>
      assetInventoryDocumentsApi.uploadAssetInventoryOwnClaimDocument({ clientId }, toUploadBody(file)),
  },
  [DocumentGroup.AssetInventoryDomesticRealEstate]: assetArea(
    DocumentGroup.AssetInventoryDomesticRealEstate,
    AssetDocumentArea.DomesticRealEstate,
    "dropzone.dnd.zoneAssetInventoryDomesticRealEstate",
    QueryKey.AssetInventoryDomesticRealEstateDocuments
  ),
  [DocumentGroup.AssetInventoryForeignRealEstate]: assetArea(
    DocumentGroup.AssetInventoryForeignRealEstate,
    AssetDocumentArea.ForeignRealEstate,
    "dropzone.dnd.zoneAssetInventoryForeignRealEstate",
    QueryKey.AssetInventoryForeignRealEstateDocuments
  ),
  [DocumentGroup.AssetInventoryBuildingSavingsContracts]: assetArea(
    DocumentGroup.AssetInventoryBuildingSavingsContracts,
    AssetDocumentArea.BuildingSavingsContracts,
    "dropzone.dnd.zoneAssetInventoryBuildingSavingsContracts",
    QueryKey.AssetInventoryBuildingSavingsContractDocuments
  ),
  [DocumentGroup.AssetInventorySavingsAccounts]: assetArea(
    DocumentGroup.AssetInventorySavingsAccounts,
    AssetDocumentArea.SavingsAccounts,
    "dropzone.dnd.zoneAssetInventorySavingsAccounts",
    QueryKey.AssetInventorySavingsAccountDocuments
  ),
  [DocumentGroup.AssetInventoryLifeInsurances]: assetArea(
    DocumentGroup.AssetInventoryLifeInsurances,
    AssetDocumentArea.LifeInsurances,
    "dropzone.dnd.zoneAssetInventoryLifeInsurances",
    QueryKey.AssetInventoryLifeInsuranceDocuments
  ),
  [DocumentGroup.AssetInventoryPensionInsurances]: assetArea(
    DocumentGroup.AssetInventoryPensionInsurances,
    AssetDocumentArea.PensionInsurances,
    "dropzone.dnd.zoneAssetInventoryPensionInsurances",
    QueryKey.AssetInventoryPensionInsuranceDocuments
  ),
  [DocumentGroup.AssetInventorySecuritiesDepots]: assetArea(
    DocumentGroup.AssetInventorySecuritiesDepots,
    AssetDocumentArea.SecuritiesDepots,
    "dropzone.dnd.zoneAssetInventorySecuritiesDepots",
    QueryKey.AssetInventorySecuritiesDepotDocuments
  ),
  [DocumentGroup.AssetInventoryCashAndBankAssets]: assetArea(
    DocumentGroup.AssetInventoryCashAndBankAssets,
    AssetDocumentArea.CashAndBankAssets,
    "dropzone.dnd.zoneAssetInventoryCashAndBankAssets",
    QueryKey.AssetInventoryCashAndBankAssetDocuments
  ),
  [DocumentGroup.AssetInventoryVehicles]: assetArea(
    DocumentGroup.AssetInventoryVehicles,
    AssetDocumentArea.Vehicles,
    "dropzone.dnd.zoneAssetInventoryVehicles",
    QueryKey.AssetInventoryVehicleDocuments
  ),
  [DocumentGroup.AssetInventoryBuildingsOnForeignLand]: assetArea(
    DocumentGroup.AssetInventoryBuildingsOnForeignLand,
    AssetDocumentArea.BuildingsOnForeignLand,
    "dropzone.dnd.zoneAssetInventoryBuildingsOnForeignLand",
    QueryKey.AssetInventoryBuildingsOnForeignLandDocuments
  ),
  [DocumentGroup.AssetInventoryTenancyRights]: assetArea(
    DocumentGroup.AssetInventoryTenancyRights,
    AssetDocumentArea.TenancyRights,
    "dropzone.dnd.zoneAssetInventoryTenancyRights",
    QueryKey.AssetInventoryTenancyRightDocuments,
    OTHER_PROPERTY_RIGHTS_SECTION
  ),
  [DocumentGroup.AssetInventoryCompanyShares]: assetArea(
    DocumentGroup.AssetInventoryCompanyShares,
    AssetDocumentArea.CompanyShares,
    "dropzone.dnd.zoneAssetInventoryCompanyShares",
    QueryKey.AssetInventoryCompanyShareDocuments,
    OTHER_PROPERTY_RIGHTS_SECTION
  ),
  [DocumentGroup.AssetInventoryCooperativeMemberships]: assetArea(
    DocumentGroup.AssetInventoryCooperativeMemberships,
    AssetDocumentArea.CooperativeMemberships,
    "dropzone.dnd.zoneAssetInventoryCooperativeMemberships",
    QueryKey.AssetInventoryCooperativeMembershipDocuments,
    OTHER_PROPERTY_RIGHTS_SECTION
  ),
  [DocumentGroup.AssetInventoryOtherPropertyRights]: assetArea(
    DocumentGroup.AssetInventoryOtherPropertyRights,
    AssetDocumentArea.OtherPropertyRights,
    "dropzone.dnd.zoneAssetInventoryOtherPropertyRights",
    QueryKey.AssetInventoryOtherPropertyRightDocuments,
    OTHER_PROPERTY_RIGHTS_SECTION
  ),
  [DocumentGroup.AssetInventoryDonations]: assetArea(
    DocumentGroup.AssetInventoryDonations,
    AssetDocumentArea.Donations,
    "dropzone.dnd.zoneAssetInventoryDonations",
    QueryKey.AssetInventoryDonationDocuments,
    DONATIONS_SECTION
  ),
};

/** Display position of each upload area in the documents overview (ascending, complete by type). */
const AREA_DISPLAY_POSITIONS: Record<AssetInventoryZone, number> = {
  [DocumentGroup.AssetInventoryBankStatement]: 0,
  [DocumentGroup.AssetInventoryDependants]: 1,
  [DocumentGroup.AssetInventoryIncome]: 2,
  [DocumentGroup.AssetInventoryExpenses]: 3,
  [DocumentGroup.AssetInventoryOwnClaims]: 4,
  [DocumentGroup.AssetInventoryDomesticRealEstate]: 5,
  [DocumentGroup.AssetInventoryForeignRealEstate]: 6,
  [DocumentGroup.AssetInventoryBuildingSavingsContracts]: 7,
  [DocumentGroup.AssetInventorySavingsAccounts]: 8,
  [DocumentGroup.AssetInventoryLifeInsurances]: 9,
  [DocumentGroup.AssetInventoryPensionInsurances]: 10,
  [DocumentGroup.AssetInventorySecuritiesDepots]: 11,
  [DocumentGroup.AssetInventoryCashAndBankAssets]: 12,
  [DocumentGroup.AssetInventoryVehicles]: 13,
  [DocumentGroup.AssetInventoryBuildingsOnForeignLand]: 14,
  [DocumentGroup.AssetInventoryTenancyRights]: 15,
  [DocumentGroup.AssetInventoryCompanyShares]: 16,
  [DocumentGroup.AssetInventoryCooperativeMemberships]: 17,
  [DocumentGroup.AssetInventoryOtherPropertyRights]: 18,
  [DocumentGroup.AssetInventoryDonations]: 19,
};

/** Every asset inventory upload area, in display order. The documents overview renders one section per entry. */
export const ASSET_INVENTORY_UPLOAD_AREAS: readonly AssetInventoryUploadArea[] = Object.values(AREAS_BY_ZONE).sort(
  (a, b) => AREA_DISPLAY_POSITIONS[a.group] - AREA_DISPLAY_POSITIONS[b.group]
);

export function getAssetInventoryUploadArea(zone: AssetInventoryZone): AssetInventoryUploadArea {
  return AREAS_BY_ZONE[zone];
}
